import React, { ForwardRefExoticComponent, FunctionComponent, RefAttributes, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Object3D, Raycaster, Intersection, Group, Object3DEventMap, FrontSide, DoubleSide } from 'three';
import { Jute } from '../..';
import { getPausableVideoIdsFromIdArray, transitionAdjustedRenderOrder } from '../../utils';
import { getAnalyticsLabelForAction } from '../../utils/analytics';
import { IEntityActionState, IEntityActionTypes, IUserActions, useActionStateStore, useDataStore, useSceneStore, useUserStore } from '../../zustand';
import EntityLevelFadeGroup from '../fadeGroups/EntityLevelFadeGroup';
import { IActionCategory, IComponentType, ISceneComp, ITriggerTypes, IVideo, IVideoPlayState, IVideoReactProps } from '../r3f-components/component-data-structure';
import { EntityErrorBoundary } from '../r3f-components/components/boundaries';
import { useGetFaceTrackedComponentIds, useGetScreenRelativeComponentIds } from '../r3f-components/hooks/useGetScreenRelativeComponentIds';
import { ITransCatTypes } from '../r3f-components/transitions';
import { ITransitionPhase } from '../r3f-components/transitions/spec';
import { filterArrayByAnother, isSafariWithAudioContextBug } from '../r3f-components/utils/general';
import {
	useAddEntityActionBehaviour,
	useGetAdjustedBorderWidth,
	useGetAdjustedMemoSpatialProps,
	useGetEntityIsHidden,
	useGetSubscribedSceneTransitionCategoryRef,
	useGetSubscribedSceneTransitionPhaseRef,
	useOnEntityTestClick,
	useResetEntityTransformsOnPlacement,
	useSceneHasRecordingButton,
} from './hocCustomHooks';
import { useAllSceneAutoplayVideoIds, useAllSceneEntityIds } from '../../hooks/general';
import { useEnableObjectLayer, useGetSceneIndexBySceneId } from './hocCustomHooks';
import { useInteraction } from '../TrackerGroups/WorldXRTracker/webxr-hooks';
import { useThree } from '@react-three/fiber';
import { filestore, FsVideo } from '@/filestore';

interface IVideoObject3D extends Object3D {
	_onVideoPointerDown: (intersections: Intersection[]) =>  void;
	_onVideoPointerup: (intersections: Intersection[]) =>  void
}

interface IVideoWrapped {
	id: string;
	sceneId: string;
	screenRelative?: boolean;
	raycast?: (_: Raycaster, intersects: Intersection<Object3D<Object3DEventMap>>[]) => void;
}

const withVideoBehaviour = (WrappedVideo: ForwardRefExoticComponent<IVideoReactProps & RefAttributes<IVideoObject3D>>): FunctionComponent<IVideoWrapped> => {
	const FuncComp: FunctionComponent<IVideoWrapped> = ({ id, sceneId, screenRelative = false, raycast }) => {
		const pointerIsDownRef = useRef(false);
		const pointerGroupRef = useRef<Group>(null)
		const {scene: threeScene} = useThree();
		const componentsById = useDataStore(({ componentsById }) => componentsById);
		const scene = useDataStore(({ componentsById }) => componentsById?.[sceneId] as ISceneComp);
		const video = useDataStore(({ componentsById }) => componentsById?.[id] as IVideo) || {};
		const sceneTransCatRef = useGetSubscribedSceneTransitionCategoryRef(sceneId);
		const sceneTransitionCategory = useSceneStore(({ transitionCategory }) => transitionCategory[sceneId]);
		const placementMode = useSceneStore(({ placementMode }) => placementMode);
		const previousSceneId = useSceneStore(({ previousSceneId }) => previousSceneId);
		const shadowsEnabled = useDataStore(({ shadowsEnabled }) => shadowsEnabled ?? true);
		const sceneTransPhaseRef = useGetSubscribedSceneTransitionPhaseRef(sceneId);
		const entityActionDispatch = useActionStateStore(({ dispatch }) => dispatch);
		const actionState = useActionStateStore(({ entityActionStateDict }) => entityActionStateDict?.[id])
		const userDispatch = useUserStore(({ dispatch }) => dispatch);
		const entityActionHandler = useAddEntityActionBehaviour(sceneId, video, sceneTransCatRef, sceneTransPhaseRef, ITriggerTypes.onFinish);
		const entityIsHidden = useGetEntityIsHidden(id) && screenRelative;
		const transitionCategory = useSceneStore((state) => state.transitionCategory[sceneId]);
		const visibleAutoplayVideoIds = useAllSceneAutoplayVideoIds(sceneId, true);
		const sceneVideoIds = useAllSceneEntityIds(sceneId, IComponentType.Video);
		const videoObject3dRef = useRef<IVideoObject3D>(null);
		const sceneHasRecordingButton = !!useSceneHasRecordingButton(sceneId);
		const sceneIndex = useGetSceneIndexBySceneId(sceneId);
		const [enableObjectTrigger, setEnabledObjectTrigger] = useState(0);
		const isGrabAndGoActive = useUserStore(({ isGrabNGoActive }) => isGrabNGoActive);

		useOnEntityTestClick({ sceneId, entity: video, sceneTransCatRef, sceneTransPhaseRef });

		useEnableObjectLayer({ object3d: pointerGroupRef, enabled: true, layer: typeof sceneIndex === 'undefined' ? undefined : sceneIndex + 1, trigger: enableObjectTrigger });

		// Ensures that any transforms due to previous animation presets are cleared out
		useResetEntityTransformsOnPlacement(id);

		// Get entity values
		const borderWidth = useGetAdjustedBorderWidth(video, screenRelative);
		const renderOrder = transitionAdjustedRenderOrder(sceneTransCatRef, (video.renderOrder ?? 0) * 20);
		const { position, scale, rotation, zeroTuple3 } = useGetAdjustedMemoSpatialProps(video, screenRelative);
		const { opacity, stopMedia, borderRadius, borderRgba, actions, filestoreId, loop, autoplay, hasAlpha, hideControls, thumbnailOnly, curvature, chromaKey, castShadow = false, receiveShadow = false, isSnappedToTarget = false, depthTest = true, billboard } = video;

		const videoFile = filestore.get<FsVideo>(filestoreId)
		const thumbnailUrl = videoFile.getThumbnailUrl()

		const screenRelativeIds = useGetScreenRelativeComponentIds(sceneId, componentsById);
		const siblings = [...(useGetFaceTrackedComponentIds(sceneId, componentsById) || []), ...scene.children];

		// Video play behaviour specific logic
		const playState = useMemo(() => {
			return Object.keys(actionState || {}).reduce((acc, actionCategory) => {
				return { ...acc, ...actionState[actionCategory as keyof IEntityActionState] };
			}, {} as IVideoPlayState)
		}, [actionState]);

		const onFullScreenCached = useCallback(
			(video: HTMLVideoElement) => {
				if (sceneTransCatRef.current !== ITransCatTypes.enter || sceneTransPhaseRef.current !== ITransitionPhase.enterFinish) return;
				// video.pause();
				userDispatch({
					type: IUserActions.SET_ENLARGED_VIDEO,
					payload: { videoRef: video, id },
				});
			},
			[sceneTransCatRef, sceneTransPhaseRef] //eslint-disable-line react-hooks/exhaustive-deps
		);

		const onVideoStateChange = useCallback(
			({ isPlaying, currentTime, hasFinished }: { isPlaying: boolean; currentTime?: number; hasFinished?: boolean }) => {
				// TODO: note that scene audio is paused within audio component which should be done here
				if (isPlaying && stopMedia && componentsById) {					
					const pausableVideoIds = getPausableVideoIdsFromIdArray(filterArrayByAnother(sceneVideoIds, [id]), componentsById);
					// console.log({pausableVideoIds})
					entityActionDispatch?.({
						type: IEntityActionTypes.PAUSE_VIDEOS_BY_ID,
						payload: { ids: pausableVideoIds },
					});
					entityActionDispatch?.({
						type: IEntityActionTypes.PAUSE_ALL_SCENE_AUDIO_BY_SCENE_ID,
						payload: { id: sceneId },
					});
					entityActionDispatch?.({
						type: IEntityActionTypes.PAUSE_BACKGROUND_AUDIO,
					});
				}

				const payload: { ids: string[]; currentTime?: number } = { ids: [id] };
				if (typeof currentTime !== 'undefined') payload.currentTime = currentTime;

				if (isPlaying) {
					Jute.logEventToAnalytics({
						category: 'Video',
						name: `Native click play`,
						label: getAnalyticsLabelForAction(IActionCategory.playVideo, video.title),
						value: 1,
					});
				}

				entityActionDispatch?.({
					type: isPlaying ? IEntityActionTypes.PLAY_VIDEOS_BY_ID : IEntityActionTypes.PAUSE_VIDEOS_BY_ID,
					payload,
				});

				if (hasFinished) {
					entityActionDispatch?.({
						type: IEntityActionTypes.PLAY_BACKGROUND_AUDIO,
					});
					if (actions?.[ITriggerTypes.onFinish]) entityActionHandler(actions[ITriggerTypes.onFinish]!);
				}
			},
			[entityActionDispatch, id, stopMedia, sceneId, entityActionHandler, actions] //eslint-disable-line react-hooks/exhaustive-deps
		);

		useEffect(() => {
			if (
				transitionCategory === ITransCatTypes.enter && // Only play on enter transition category
				visibleAutoplayVideoIds.includes(id) && // Only autoplay videos
				(previousSceneId !== null || Jute.isIframe) // First scene autoplay videos are played from App component as onLaunch callback to ensure direct interaction
			) {
				entityActionDispatch?.({
					type: IEntityActionTypes.PLAY_VIDEOS_BY_ID,
					payload: { ids: [id] },
				});
			}
		}, [previousSceneId, transitionCategory, placementMode, componentsById]);


		// // // If a video is paused by iOS, mute and play it ( best we can do ) as likely caused by safari blocking multiple audio tracks
		// const isSafariBrowser = isSafari();
		// const isIos = /iP(ad|od|hone)/i.test(window.navigator.userAgent);
		// const videoElement = useGetHlsCache(id)?.[1];

		// useEffect(() => {
		// 	if (!videoElement) return;
		// 	if (isIos && isSafariBrowser) {
		// 		//if (!videoElement.loop) return;
		// 		videoElement.addEventListener('pause', (e) => {
		// 			if (e.target) {
		// 				const video = e.target as HTMLVideoElement;
		// 				const videoId = video.id;
		// 				if (useActionStateStore.getState().entityActionStateDict[videoId] && useActionStateStore.getState().entityActionStateDict[videoId]?.playVideo?.isPlaying) {
		// 					video.muted = true;
		// 					video.play();
		// 				}
		// 			}
		// 		});
		// 	}
		// }, [videoElement])

		useInteraction(pointerGroupRef, 'onSelectStart', e => {
			videoObject3dRef?.current?._onVideoPointerDown(e.intersections)
		})

		useInteraction(pointerGroupRef, 'onSelectEnd', e => {
			videoObject3dRef?.current?._onVideoPointerup(e.intersections)
		})

		return (
			<group name="ENTITY_VISIBLE_GROUP" visible={!entityIsHidden}>
				<EntityLevelFadeGroup sceneId={sceneId} entityId={id} position={zeroTuple3} screenRelative={screenRelative}>
					<EntityErrorBoundary renderOrder={renderOrder} scale={scale} rotation={rotation} position={position}>
						<group name={`toggleVisibilityGroup-${id}`}>
							<group ref={pointerGroupRef} name={`sceneId_${sceneId}`} onPointerUp={() => (pointerIsDownRef.current = false)}>
								<WrappedVideo
									id={id}
									ref={videoObject3dRef}
									renderOrder={renderOrder}
									rotation={rotation}
									position={position}
									siblings={siblings}
									enabled={sceneTransitionCategory === ITransCatTypes.enter && !placementMode}
									isPlaying={playState.isPlaying}
									videoRunCounter={playState.videoRunCounter}
									scale={scale}
									opacity={opacity}
									thumbnailUrl={thumbnailUrl}
									curvature={curvature}
									chromaKey={chromaKey}
									videoUrl={videoFile.getPlaylistUrl()}
									loop={loop}
									autoplay={autoplay}
									hasAlpha={hasAlpha}
									hideControls={hideControls}
									thumbnailOnly={thumbnailOnly}
									borderRadius={borderRadius}
									borderRgba={borderRgba}
									borderWidth={borderWidth}
									side={isSnappedToTarget ? FrontSide :  DoubleSide}
									castShadow={shadowsEnabled && castShadow}
									receiveShadow={shadowsEnabled && receiveShadow}
									audioContext={!isSafariWithAudioContextBug() ? Jute.audioContext : undefined}
									audioStreamDestination={!isSafariWithAudioContextBug() ? Jute.audioStreamDestination : undefined}
									separateAudioFromVideo={(Jute.isNativeHlsSupported && !isSafariWithAudioContextBug()) && sceneHasRecordingButton}
									raycast={raycast}
									onFullScreen={onFullScreenCached}
									onVideoStateChange={onVideoStateChange}
									screenRelativeIds={screenRelativeIds}
									threeScene={threeScene}
									addVideoToDom={Jute.isAppClip}
									onMount={() => setEnabledObjectTrigger(prev => prev + 1)}
									depthTest={depthTest}
									animationTransformGroupPrefix='transformGroup-'
									billboard={billboard}
									isCameraOrbiting={isGrabAndGoActive}
									rotateNoBillboardOuterGroup={false}
								/>
							</group>
						</group>
					</EntityErrorBoundary>
				</EntityLevelFadeGroup>
			</group>
		);
	};
	return FuncComp;
};

export default withVideoBehaviour;
